import React from 'react'
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Theme, createStyles } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ApiService from '../../service/ApiService';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { SocialIcon } from 'react-social-icons';
import Paper from '@mui/material/Paper';
import { apiGatewayUrlParam } from '../../config/ServiceConfig';
import { useAuth } from '../../hooks';

interface MyStyles {
    paper: string;
    fullWidth: string;
    inProgress: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
        marginTop: theme.spacing(4),
        textAlign: "center",
        maxWidth: theme.breakpoints.values["sm"],
        margin: "auto",
        paddingTop: theme.spacing(4),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    fullWidth: {
        width: '100%'
    },
    inProgress: {
        alignSelf: "center"
    }
  })
);

enum Category {
    RequestQRCodes = "Request-QR-Codes",
    ReportIssue = "Report-Issue",
    FeatureRequest = "Feature-Request",
    Question = "Question"
}

const apiService = new ApiService(apiGatewayUrlParam);

const ContactUs: React.FunctionComponent = (props) => {
    const classes = useStyles() as MyStyles;

    // Use our new state management hooks
    const { isAuthenticated, isLoading } = useAuth();

    // Local state for form data
    const [category, setCategory] = useState(Category.RequestQRCodes);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const reset = () => {
        setCategory(Category.RequestQRCodes);
        setTitle("");
        setContent("");
        setIsSubmitting(false);
    }

    const handleCategoryChange = (event: SelectChangeEvent<Category>) => {
        setCategory(event.target.value as Category);
    };

    const handleTitleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setTitle(event.target.value as string);
    };

    const handleContentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setContent(event.target.value as string);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        
        try {
            const data = await apiService.postRequest(`contactUs`, {
                category,
                title,
                content
            });
            console.log("Success submitting message!");
            console.log(JSON.stringify(data));

            setShowSuccess(true);
            reset();
        } catch (error) {
            console.error("Error submitting message:", error);
            setIsSubmitting(false);
        }
    }

    const renderForm = () => {
        // className was not working
        const socialIconStyle = {
            height: 35,
            width: 35,
            marginLeft: 15,
            marginRight: 15
        };
        return (
            <Paper className={classes.paper}>
                <Grid
                    container
                    direction="column"
                    spacing={3}
                    alignItems="center"
                >
                    <Grid item>
                        <Typography variant="h3">Contact Us</Typography>
                    </Grid>
                    <Grid
                        item
                        container
                        direction="row"
                        justifyContent="center"
                    >
                        <Grid item>
                            <SocialIcon url="https://www.instagram.com/KandiLoveLLC/" style={socialIconStyle} />
                        </Grid>
                        <Grid item>
                            <SocialIcon url="https://www.facebook.com/KandiLoveLLC" style={socialIconStyle} />
                        </Grid>
                        <Grid item>
                            <SocialIcon url="https://twitter.com/KandiLoveLLC" style={socialIconStyle} />
                        </Grid>
                        <Grid item>
                            <SocialIcon url="https://discord.gg/WanJqen9" style={socialIconStyle} />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Typography variant="body2">
                            Have questions, comments or concerns?
                            Use the form below to send us a message, and someone from our staff will reply
                            to the email address you used when signing up for KandiLove.
                        </Typography>
                    </Grid>
                    <Grid item className={classes.fullWidth}>
                        <InputLabel id="category-input-label">What is this about?</InputLabel>
                        <Select
                            labelId="category-input-label"
                            id="category-input"
                            value={category}
                            onChange={handleCategoryChange}
                            className={classes.fullWidth}
                        >
                            <MenuItem value={Category.ReportIssue}>Report an issue</MenuItem>
                            <MenuItem value={Category.FeatureRequest}>Request a feature</MenuItem>
                            <MenuItem value={Category.Question}>General Question?</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item className={classes.fullWidth}>
                        <TextField
                            id="title-input"
                            label="Message Title"
                            className={classes.fullWidth}
                            value={title}
                            onChange={handleTitleChange}
                        />
                    </Grid>
                    <Grid item className={classes.fullWidth}>
                        <TextField 
                            className={classes.fullWidth}
                            multiline
                            rows={8}
                            id="content-input"
                            label="Message"
                            value={content}
                            onChange={handleContentChange}
                        />
                    </Grid>
                    { isSubmitting ? (
                        <Grid item className={classes.inProgress}>
                            <CircularProgress 
                                color="primary" 
                            />
                        </Grid>
                    ) : 
                    (
                        <Grid item className={classes.fullWidth}>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!(category && title && content)}
                                fullWidth
                            >
                                Submit!
                            </Button>
                        </Grid>
                    )}
                </Grid>
                <Snackbar open={showSuccess} autoHideDuration={3000} onClose={()=>setShowSuccess(false)}>
                    <MuiAlert onClose={()=>setShowSuccess(false)} severity="success">
                        Message has been submitted!
                    </MuiAlert>
                </Snackbar>
            </Paper>
        );
    }

    // Render loading state
    if (isLoading) {
        return (
            <Paper className={classes.paper}>
                <Grid container justifyContent="center" style={{ padding: '2rem' }}>
                    <CircularProgress />
                </Grid>
            </Paper>
        );
    }

    // Render based on authentication status
    if (isAuthenticated) {
        return renderForm();
    } else {
        return (
            <Paper className={classes.paper}>
                <Typography variant="body2">
                    You must first sign in to send us a message.
                </Typography>
            </Paper>
        );
    }
}

export default ContactUs;
